const SUPPORTED_COUNTRY_LANGUAGE_MAP: Record<
	string,
	'NORSK' | 'DANSK' | 'SVENSK' | 'ENG'
> = {
	NO: 'NORSK',
	DK: 'DANSK',
	SE: 'SVENSK',
};

const DEFAULT_LOADING_BY_LANG: Record<'NORSK' | 'DANSK' | 'SVENSK' | 'ENG', string> = {
	NORSK: 'Laster inn',
	DANSK: 'Indlæser',
	SVENSK: 'Laddar',
	ENG: 'Loading',
};

const COUNTRY_ENDPOINTS: Array<{
	url: string;
	parse: (data: any) => { code: string; name?: string } | null;
}> = [
	{
		// Ultra simple: returns { country: 'US' }
		url: 'https://api.country.is',
		parse: (d) =>
			d && typeof d.country === 'string' ? { code: d.country } : null,
	},
	{
		// Public & generous: returns rich data (no key)
		url: 'https://ipwho.is/',
		parse: (d) =>
			d && d.success && typeof d.country_code === 'string'
				? { code: d.country_code, name: d.country }
				: null,
	},
	{
		// Your original (can 429): returns { country_code: 'NO', country_name: 'Norway' }
		url: 'https://ipapi.co/json/',
		parse: (d) =>
			d && typeof d.country_code === 'string'
				? { code: d.country_code, name: d.country_name }
				: null,
	},
];

// Small fetch with timeout + clear 429 handling
async function fetchJsonWithTimeout(url: string, ms = 3500): Promise<any> {
	const ctrl = new AbortController();
	const id = setTimeout(() => ctrl.abort(), ms);
	try {
		const res = await fetch(url, { signal: ctrl.signal, cache: 'no-store' });
		// 429 => Too Many Requests
		if (res.status === 429) {
			const err: any = new Error('Rate limited');
			err.code = 429;
			throw err;
		}
		if (!res.ok) throw new Error(`HTTP ${res.status}`);
		return await res.json();
	} finally {
		clearTimeout(id);
	}
}

// Resolve a country code using endpoints, session cache, or navigator.language.
export async function resolveCountry(
	dispatch: any
): Promise<{ code: string; name?: string }> {
	// 1) Session cache (avoid repeat calls within the session)
	const cached = sessionStorage.getItem('geo.country');
	if (cached) {
		try {
			const parsed = JSON.parse(cached);
			if (parsed?.code && typeof parsed.code === 'string') {
				// Also reflect in state quickly
				dispatch({ type: 'SET_COUNTRY', payload: parsed.name || parsed.code });
				return parsed;
			}
		} catch {}
	}

	// 2) Try endpoints in order
	for (const ep of COUNTRY_ENDPOINTS) {
		try {
			const data = await fetchJsonWithTimeout(ep.url);
			const parsed = ep.parse(data);
			if (parsed && parsed.code) {
				// Cache for this session
				sessionStorage.setItem('geo.country', JSON.stringify(parsed));
				dispatch({ type: 'SET_COUNTRY', payload: parsed.name || parsed.code });
				return parsed;
			}
		} catch (_e) {
			// ignore and continue to the next endpoint
		}
	}

	// 3) Fallback: try navigator.language region (e.g., "en-US" -> "US")
	const lang =
		(navigator.languages && navigator.languages[0]) || navigator.language || '';
	const regionMatch = lang.match(/[-_](\w{2})$/i);
	const regionCode = regionMatch ? regionMatch[1].toUpperCase() : '';
	if (regionCode) {
		const fallback = { code: regionCode };
		sessionStorage.setItem('geo.country', JSON.stringify(fallback));
		dispatch({ type: 'SET_COUNTRY', payload: regionCode });
		return fallback;
	}

	// 4) Final fallback: unknown
	const fallback = { code: 'ZZ' };
	sessionStorage.setItem('geo.country', JSON.stringify(fallback));
	dispatch({ type: 'SET_COUNTRY', payload: null });
	return fallback;
}
